import diff from "immutablediff";
import patch from "immutablepatch";
import { fromJS, List, Map, Set } from "immutable";
import { ItemFields, ValueTypes } from "constants/product-collection";
import memoizeOne from "memoize-one";

interface IDelta {
    path: string[];
    value: any;
    op: "replace" | "add" | "remove";
}

/**
 * Merges data coming in from server if product has been updated
 * @param currentItem - the item data in the assortment, what you see in AG grid
 * @param currentProduct - latest state of the product data
 * @param oldProduct - product data in the state from the last time the assortment item was updated
 * @returns updated assortment item
 */

export const syncPatch = ({ currentItem, currentProduct, oldProduct }) => {
    const deltas: Array<Map<keyof IDelta, IDelta[keyof IDelta]>> = diff(oldProduct, currentProduct);
    let updatedItem = patch(currentItem, deltas);
    deltas.forEach((delta) => {
        const deltaValue = delta.get("value");
        const path = delta.get("path").substr(1).split("/");
        const op = delta.get("op");
        if (typeof deltaValue === "string" && op === "replace") {
            const oldValue = currentItem.getIn(path);
            if (deltaValue !== oldValue) {
                const lastPart = path.pop();
                updatedItem = updatedItem.setIn(
                    path,
                    fromJS({
                        [lastPart]: deltaValue,
                    })
                );
            }
        } else if (typeof deltaValue === "object" && op === "add") {
            updatedItem = updatedItem.setIn(path, fromJS(deltaValue));
        } else if (deltaValue === undefined && op === "remove") {
            const oldValueToPreserve = currentItem.getIn(path);
            updatedItem = updatedItem.setIn(path, fromJS(oldValueToPreserve));
        }
    });
    return updatedItem;
};

export const getFieldNameToDisplayPropertyMap = memoizeOne((itemDisplayProperty: List<any>) =>
    itemDisplayProperty.reduce((acc, curr) => acc.set(curr.get("field_name"), curr), Map<string, any>())
);

export const syncDateFields = ({
    currentItem,
    currentProduct,
    oldProduct,
    itemDisplayProperty,
}: {
    currentItem: Map<string, any>;
    currentProduct: Map<string, any>;
    oldProduct: Map<string, any>;
    itemDisplayProperty: List<any>;
}) => {
    const keys = Set(oldProduct.get(ItemFields.Fields).keySeq().concat(currentProduct.get(ItemFields.Fields).keySeq()));
    const propMap = getFieldNameToDisplayPropertyMap(itemDisplayProperty);
    keys.forEach((key) => {
        if (propMap.getIn([key, "value_type"]) !== ValueTypes.DATE) {
            return;
        }

        const fieldPath = [ItemFields.Fields, key, "struct_value"];
        const correctedFieldPath = [ItemFields.Fields, key, "date_value"];
        const currentProductField = currentProduct.getIn(fieldPath);
        const oldProductField = oldProduct.getIn(fieldPath);

        // Here we also remove the struct_value path to ensure compatibility for old assortments after product synchronization.
        if (currentProductField) {
            currentItem = currentItem.setIn(correctedFieldPath, currentProductField).deleteIn(fieldPath);
        } else if (oldProductField) {
            currentItem = currentItem.deleteIn([ItemFields.Fields, key]);
        }
    });
    return currentItem;
};
